import {
  getGetClientProfileQueryKey,
  useGetClientProfile,
} from '@biasafe.queries/clients-client-id-profile/clients-client-id-profile';
import { SearchTypeChip } from './SearchTypeChip';
import { searchCodeStyle, searchNameStyle } from './searchAppearance';
import type { ClientResponse } from '@biasafe.models';
import { deriveClientType } from '@/features/Clients/clientStatus';
import { useAdvisorCopy } from '@/shared/hooks/useAdvisorCopy';
import { DirectoryAvatar } from '@/features/Clients/components/DirectoryAvatar';
import { CountryFlag } from '@/shared/components/CountryFlag';
import { countryName, orderCitizenships } from '@/shared/geo';

/** Reuse the CRM's authenticated photo and citizenship semantics for matched rows only. */
export function ClientSearchIdentity({
  client,
  showCode = false,
}: {
  client: ClientResponse;
  showCode?: boolean;
}) {
  const { data: profile } = useGetClientProfile(client.id, {
    query: {
      queryKey: getGetClientProfileQueryKey(client.id),
      staleTime: 60_000,
      retry: false,
      refetchOnWindowFocus: false,
    },
  });
  const flags = orderCitizenships(
    client.clientType === 'CORPORATE' || client.clientType === 'TRUST'
      ? [String(profile?.saved?.registration_jurisdiction ?? '')]
      : profile?.citizenships?.length
        ? profile.citizenships
        : [profile?.citizenship ?? ''],
  );
  return (
    <>
      <span className="row-span-2 sm:row-span-1">
        <DirectoryAvatar client={client} shape="square" />
      </span>
      {showCode && (
        <span className={searchCodeStyle}>{client.referenceCode}</span>
      )}
      <span
        className={
          showCode ? searchNameStyle : 'flex min-w-0 flex-1 items-center gap-2'
        }
      >
        <span className="min-w-0 truncate">
          {client.displayName || client.referenceCode}
        </span>
        {flags.length > 0 && (
          <span className="inline-flex max-w-[45%] shrink-0 flex-wrap items-center gap-1">
            {flags.map((code) => (
              <span key={code} className="inline-flex items-center">
                <CountryFlag
                  code={code}
                  className="h-4 w-6 shrink-0 rounded-xs object-cover ring-1 ring-border/40"
                />
                <span className="sr-only">{countryName(code)}</span>
              </span>
            ))}
          </span>
        )}
      </span>
    </>
  );
}

/** Same dimensions and emphasis as security-type chips, with CRM relationship labels. */
export function ClientSearchTypeBadge({ status }: { status: string }) {
  const copy = useAdvisorCopy();
  const type = deriveClientType(status);
  return (
    <SearchTypeChip
      label={copy(type)}
      tone={type === 'Client' ? 'primary' : 'neutral'}
    />
  );
}
